import type { SizeVariants } from '~kui/types/kui';

/**
 * Autoplay plugin configuration options
 * Follows Embla Carousel Autoplay plugin API
 */
export interface CarouselAutoScrollOptions {
  speed?: number;
  startDelay?: number;
  direction?: 'forward' | 'backward';
  playOnInit?: boolean;
  stopOnInteraction?: boolean;
  stopOnMouseEnter?: boolean;
  stopOnFocusIn?: boolean;
}

export interface CarouselAutoplayOptions {
  /** Time in ms between auto-advancing slides */
  delay?: number;
  /** Stop autoplay when mouse enters carousel */
  stopOnMouseEnter?: boolean;
  /** Stop autoplay when user interacts (drag, click arrows/dots) */
  stopOnInteraction?: boolean;
  /** Stop autoplay when a focusable element receives focus */
  stopOnFocusIn?: boolean;
  /** Start playing immediately on initialization */
  playOnInit?: boolean;
}

/**
 * UI control options for the carousel
 */
export interface CarouselControlOptions {
  /** Show previous/next arrow buttons */
  showArrows?: boolean;
  /** Show dot indicators */
  showDots?: boolean;
  /** Size of dot indicators */
  dotsSize?: Extract<SizeVariants, 'sm' | 'md'>;
}

/**
 * Core carousel behavior options
 * Maps to Embla Carousel options
 */
export interface CarouselCoreOptions {
  /** Enable infinite looping */
  loop?: boolean;
  /** Slide alignment within viewport */
  align?: 'start' | 'center' | 'end';
  /** Number of slides to scroll at once (or 'auto' for all visible) */
  slidesToScroll?: number | 'auto';
  /** Transition duration in ms */
  duration?: number;
}

/**
 * Custom CSS class overrides
 */
export interface CarouselClassOptions {
  /** Wrapper element class */
  className?: string;
  /** Viewport element class */
  viewportClassName?: string;
  /** Container element class */
  containerClassName?: string;
  /** Individual slide element class */
  slideClassName?: string;
}

/**
 * Complete carousel props interface
 */
export interface CarouselProps
  extends CarouselControlOptions, CarouselCoreOptions, CarouselClassOptions {
  /**
   * Enable autoplay functionality
   * - true: Use default autoplay settings
   * - CarouselAutoplayOptions: Custom autoplay configuration
   * - false/undefined: No autoplay
   */
  autoplay?: boolean | CarouselAutoplayOptions;
}

/**
 * Default values for carousel options
 */
export const CAROUSEL_DEFAULTS = {
  // UI Controls
  showArrows: true,
  arrowsPosition: 'outside' as const,
  showDots: true,
  dotsSize: 'sm' as const,

  // Core Options
  loop: true,
  align: 'center' as const,
  slidesToScroll: 1,
  duration: 25,

  // Autoplay
  autoplayDelay: 5000,
  autoplayStopOnMouseEnter: true,
  autoplayStopOnInteraction: false,
  autoplayStopOnFocusIn: false,
  autoplayPlayOnInit: true,

  // Classes
  className: '',
  containerClassName: undefined,
  slideClassName: undefined,
  viewportClassName: undefined,

  dragFree: false,
} as const;

/**
 * Type helper to get resolved autoplay options
 */
export type ResolvedAutoplayOptions = CarouselAutoplayOptions | false;

/**
 * Helper to resolve autoplay prop into plugin options
 */
export const resolveAutoplayOptions = (
  autoplay?: boolean | CarouselAutoplayOptions,
): ResolvedAutoplayOptions => {
  if (!autoplay) {
    return false;
  }

  if (autoplay === true) {
    return {
      delay: CAROUSEL_DEFAULTS.autoplayDelay,
      stopOnMouseEnter: CAROUSEL_DEFAULTS.autoplayStopOnMouseEnter,
      stopOnInteraction: CAROUSEL_DEFAULTS.autoplayStopOnInteraction,
      stopOnFocusIn: CAROUSEL_DEFAULTS.autoplayStopOnFocusIn,
      playOnInit: CAROUSEL_DEFAULTS.autoplayPlayOnInit,
    };
  }

  return {
    delay: autoplay.delay ?? CAROUSEL_DEFAULTS.autoplayDelay,
    stopOnMouseEnter:
            autoplay.stopOnMouseEnter ?? CAROUSEL_DEFAULTS.autoplayStopOnMouseEnter,
    stopOnInteraction:
            autoplay.stopOnInteraction ?? CAROUSEL_DEFAULTS.autoplayStopOnInteraction,
    stopOnFocusIn:
            autoplay.stopOnFocusIn ?? CAROUSEL_DEFAULTS.autoplayStopOnFocusIn,
    playOnInit: autoplay.playOnInit ?? CAROUSEL_DEFAULTS.autoplayPlayOnInit,
  };
};
